<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n, type Locale } from '@/i18n';
import { showBootOverlay } from '@/services/bootOverlayService';

const emit = defineEmits<{ (event: 'scroll-to', section: string): void }>();
const { t, locale, setLocale } = useI18n();

function emitScrollTo(section: string) {
  emit('scroll-to', section);
}

const scrolled = ref(false);
const langOpen = ref(false);
const langRef = ref<HTMLElement | null>(null);

const langs: Array<{ code: Locale; label: string; flag: string }> = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
];

function onScroll() {
  scrolled.value = window.scrollY > 20;
}

function onClickOutside(e: MouseEvent) {
  if (langRef.value && !langRef.value.contains(e.target as Node)) {
    langOpen.value = false;
  }
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true });
  document.addEventListener('click', onClickOutside);
});
onUnmounted(() => {
  window.removeEventListener('scroll', onScroll);
  document.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <header :class="['lp-header', { 'lp-header--scrolled': scrolled }]">
    <div class="lp-container lp-header-inner">
      <a href="/landing" class="lp-logo" aria-label="SwapWish TCG">
        <img src="/logo.svg" alt="SwapWish TCG" class="lp-logo-img" />
        <span class="lp-logo-name">SwapWish <em>TCG</em></span>
      </a>

      <nav class="lp-nav" :aria-label="t('landing_header_features')">
        <button type="button" class="lp-nav-link" @click.prevent="emitScrollTo('features')">
          {{ t('landing_header_features') }}
        </button>
        <button type="button" class="lp-nav-link" @click.prevent="emitScrollTo('how-it-works')">
          {{ t('landing_header_how_it_works') }}
        </button>
        <button type="button" class="lp-nav-link" @click.prevent="emitScrollTo('download')">
          {{ t('landing_header_download') }}
        </button>
      </nav>

      <div class="lp-lang-switcher" ref="langRef">
        <button
          class="lp-lang-btn"
          @click.stop="langOpen = !langOpen"
          :aria-expanded="langOpen"
          aria-haspopup="listbox"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z" fill="currentColor"/>
          </svg>
          <span class="lp-lang-label">{{ locale.toUpperCase() }}</span>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true"
            :style="{ transform: langOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.18s' }">
            <path d="M7 10l5 5 5-5z" fill="currentColor"/>
          </svg>
        </button>

        <Transition name="lp-dropdown">
          <div v-if="langOpen" class="lp-lang-dropdown" role="listbox">
            <button
              v-for="l in langs"
              :key="l.code"
              class="lp-lang-option"
              :class="{ 'lp-lang-option--active': locale === l.code }"
              role="option"
              :aria-selected="locale === l.code"
              @click="setLocale(l.code); langOpen = false"
            >
              <span class="lp-lang-flag">{{ l.flag }}</span>
              <span>{{ l.label }}</span>
              <svg v-if="locale === l.code" width="14" height="14" viewBox="0 0 24 24" fill="none" class="lp-lang-check">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </Transition>
      </div>

      <!-- Browser: mostra subito la SwapWishLoadingView (overlay di boot) e
           naviga al login; l'overlay cala quando LoginView è pronta. -->
      <router-link
        to="/login"
        class="lp-btn-login"
        @click="showBootOverlay()"
      >
        {{ t('landing_header_login') }}
      </router-link>
    </div>
  </header>
</template>

<style scoped>
.lp-header {
  position: fixed;
  inset: 0 0 auto;
  z-index: 100;
  transition:
    background var(--md-duration-medium2) var(--md-easing-standard),
    border-color var(--md-duration-medium2) var(--md-easing-standard),
    box-shadow var(--md-duration-medium2) var(--md-easing-standard);
  border-bottom: 1px solid transparent;
}
.lp-header--scrolled {
  background: rgba(13, 16, 24, 0.82);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  backdrop-filter: blur(20px) saturate(1.4);
  border-bottom-color: rgba(255, 255, 255, 0.07);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}
.lp-container {
  max-width: 1160px;
  margin-inline: auto;
  padding-inline: 24px;
}
.lp-header-inner {
  display: flex;
  align-items: center;
  gap: 24px;
  height: 64px;
}
.lp-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
}
.lp-logo-img { width: 36px; height: 36px; }
.lp-logo-name {
  font-weight: 700;
  font-size: 17px;
  color: var(--text-main);
  letter-spacing: -0.01em;
}
.lp-logo-name em { font-style: normal; color: var(--primary-indigo); }
.lp-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
}
.lp-nav-link {
  display: inline-flex;
  align-items: center;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-muted);
  border-radius: 10px;
  background: transparent;
  border: none;
  text-decoration: none;
  cursor: pointer;
  transition:
    color var(--md-duration-short4) var(--md-easing-standard),
    background var(--md-duration-short4) var(--md-easing-standard);
}
.lp-nav-link:hover { color: var(--text-main); background: rgba(255, 255, 255, 0.05); }

/* Language switcher */
.lp-lang-switcher { position: relative; flex-shrink: 0; }
.lp-lang-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--text-muted);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition:
    color var(--md-duration-short4) var(--md-easing-standard),
    background var(--md-duration-short4) var(--md-easing-standard),
    border-color var(--md-duration-short4) var(--md-easing-standard);
}
.lp-lang-btn:hover {
  color: var(--text-main);
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.18);
}
.lp-lang-label { letter-spacing: 0.02em; }
.lp-lang-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 148px;
  background: #161e32;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  padding: 6px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  z-index: 10;
}
.lp-lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: none;
  border-radius: 9px;
  color: var(--text-muted);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background var(--md-duration-short4), color var(--md-duration-short4);
}
.lp-lang-option:hover { background: rgba(255, 255, 255, 0.06); color: var(--text-main); }
.lp-lang-option--active { color: var(--text-main); font-weight: 600; }
.lp-lang-flag { font-size: 16px; line-height: 1; }
.lp-lang-check { margin-left: auto; color: var(--primary-indigo); }

.lp-btn-login {
  display: inline-flex;
  align-items: center;
  padding: 9px 22px;
  background: var(--primary-indigo);
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  border-radius: 12px;
  letter-spacing: 0.01em;
  text-decoration: none;
  flex-shrink: 0;
  transition:
    background var(--md-duration-short4) var(--md-easing-standard),
    box-shadow var(--md-duration-short4) var(--md-easing-standard),
    transform var(--md-duration-short4) var(--md-easing-spring);
}
.lp-btn-login:hover {
  background: var(--primary-indigo-strong);
  box-shadow: 0 6px 20px rgba(124, 140, 255, 0.38);
  transform: translateY(-1px);
}
.lp-btn-login:active { transform: translateY(0); }

.lp-dropdown-enter-active,
.lp-dropdown-leave-active {
  transition: opacity 0.15s var(--md-easing-standard), transform 0.15s var(--md-easing-standard);
}
.lp-dropdown-enter-from,
.lp-dropdown-leave-to { opacity: 0; transform: translateY(-6px) scale(0.97); }

@media (max-width: 640px) {
  .lp-nav { display: none; }
  .lp-header-inner { gap: 8px; }
  .lp-lang-label { display: none; }
  .lp-btn-login { margin-left: auto; }
}
</style>
